import { WHERE_THE_LAKE_ENDS as STORY_EPISODE, type EpisodeShot } from '../story/episode';
import type { DirectionSettings, StudioAsset, StudioEpisode } from './types';

const shotLetter = (index: number): string => String.fromCharCode(65 + index);
const lensFromFov = (fov: number): number => Math.round(18 / Math.tan((fov * Math.PI) / 360));

const directionFrom = (shot: EpisodeShot, timeOfDay: DirectionSettings['timeOfDay']): DirectionSettings => ({
  cameraPreset: shot.camera.preset,
  lensMm: lensFromFov(shot.camera.fov),
  timeOfDay,
  action: shot.direction[0]?.action ?? 'idle',
  expression: shot.direction[0]?.expression ?? 'curious',
});

/** Studio-shaped view of the verified episode in src/story/episode.ts. */
export const WHERE_THE_LAKE_ENDS: StudioEpisode = {
  id: STORY_EPISODE.id,
  number: '01',
  title: STORY_EPISODE.title,
  logline: STORY_EPISODE.logline,
  scenes: STORY_EPISODE.scenes.map((scene) => ({
    id: scene.id,
    number: scene.number,
    title: scene.name,
    summary: scene.summary,
    weather: `${scene.location} · ${scene.timeOfDay.replace('-', ' ')}`,
    storyBeat: scene.storyBeat,
    shots: scene.shots.map((shot, index) => ({
      id: shot.id,
      sceneId: scene.id,
      number: `${scene.number}${shotLetter(index)}`,
      title: shot.name,
      duration: shot.duration,
      color: scene.color,
      note: shot.note ?? shot.description,
      sourceDescription: shot.description,
      direction: directionFrom(shot, scene.timeOfDay),
    })),
  })),
};

export const STUDIO_ASSETS: StudioAsset[] = [
  { id: 'asset-arlo', name: 'Arlo', kind: 'Character', glyph: 'A', swatch: '#bd6f52', status: 'Ready' },
  { id: 'asset-pasha', name: 'Pasha', kind: 'Character', glyph: 'P', swatch: '#d3a756', status: 'Ready' },
  { id: 'asset-liam', name: 'Liam', kind: 'Character', glyph: 'L', swatch: '#7596a6', status: 'Ready' },
  { id: 'asset-milo', name: 'Milo', kind: 'Character', glyph: 'M', swatch: '#79906d', status: 'Ready' },
  { id: 'asset-zara', name: 'Zara', kind: 'Character', glyph: 'Z', swatch: '#a77a91', status: 'Ready' },
  { id: 'asset-tuk', name: 'Tuk', kind: 'Character', glyph: 'T', swatch: '#8d7058', status: 'Draft' },
  { id: 'asset-mom', name: 'Mom', kind: 'Character', glyph: 'M', swatch: '#c58a72', status: 'Ready' },
  { id: 'asset-dad', name: 'Dad', kind: 'Character', glyph: 'D', swatch: '#6f7f73', status: 'Ready' },
  { id: 'asset-mirror', name: 'Cabin mirror', kind: 'Prop', glyph: '◯', swatch: '#8fc3d8', status: 'Ready' },
  { id: 'asset-pawprint', name: 'Big pawprint', kind: 'Prop', glyph: '●', swatch: '#765f4f', status: 'Ready' },
  { id: 'asset-bridge', name: 'Rope bridge', kind: 'Environment', glyph: '≋', swatch: '#9c7a50', status: 'Ready' },
  { id: 'asset-lantern', name: 'Ranger lantern', kind: 'Prop', glyph: '✦', swatch: '#d6a24d', status: 'Ready' },
  { id: 'asset-station', name: 'Ranger station', kind: 'Environment', glyph: '⌂', swatch: '#687e68', status: 'Draft' },
];
